"use client";

import React from "react";
import { useProfile } from "../hooks/useProfile";

import { usePathname } from "next/navigation";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { getOneSignal } from "../helpers/onesignal";
import { useIsEmbedded } from "../hooks/useIsEmbedded";
import { useSession } from "./SupabaseProvider";

interface MainProps {
  children: React.ReactNode;
}

export default function Main({ children }: MainProps) {
  const { data: profile } = useProfile();
  const isEmbedded = useIsEmbedded();
  const isIntercomAllowed = useFeatureFlagEnabled("free-trial-allow-list");
  const session = useSession();
  const intercomBootedRef = useRef(false);

  useEffect(() => {
    if (!profile) return;
    try {
      // Skip OneSignal entirely on embed routes — the iframe player has no
      // notification UI and the SDK fetch is pure waste there. For other
      // surfaces, lazy-load the SDK and call `.login()` once it resolves.
      const logInOneSignal = async () => {
        if (isEmbedded) return;
        try {
          const OneSignal = await getOneSignal();
          await OneSignal.login(profile.id);
          console.log("OneSignal logged in", {
            externalId: profile.id,
            optedIn: OneSignal.User.PushSubscription.optedIn,
            id: OneSignal.User.PushSubscription.id,
            token: OneSignal.User.PushSubscription.token,
            permissionNative: OneSignal.Notifications.permissionNative,
          });
        } catch (e) {
          console.warn(`OneSignal: ${e}`);
        }
      };
      logInOneSignal();


      // Secure mode: fetch JWT from API and boot Intercom with token
      const initIntercomSecure = async () => {
        if (!isIntercomAllowed) return;
        if (!session?.access_token) return;
        if (intercomBootedRef.current) return;
        try {
          // Load the widget script on demand — nothing else injects it on
          // (app) routes since the May 2026 perf refactor. No-ops in iframes.
          const { boot } = await import("../lib/intercom");
          await boot();
          if (!window.Intercom) return;
          const res = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/users/intercom`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${session.access_token}`,
              },
            }
          );
          if (!res.ok) throw new Error("Failed to fetch Intercom token");
          const data = (await res.json()) as { token?: string };
          if (!data?.token) throw new Error("Token missing in response");

          // @ts-ignore
          window.Intercom("boot", {
            api_base: "https://api-iam.intercom.io",
            app_id: "o7y22sae",
            intercom_user_jwt: data.token,
            user_id: profile?.id,
            email: profile?.email,
            subscription_status: profile?.subscriptionStatus,
            plan: profile?.plan,
            session_duration: 86400000,
          });
          intercomBootedRef.current = true;

          if (isEmbedded) {
            // @ts-ignore
            window.Intercom("update", { hide_default_launcher: true });
          }
        } catch (err) {
          console.warn(`Intercom secure mode: ${err}`);
        }
      };

      initIntercomSecure();
    } catch (e) {
      console.warn(`Tidio: ${e}`);
    }
  }, [profile, session?.access_token, isIntercomAllowed, isEmbedded]);

  useEffect(() => {
    if (!isIntercomAllowed) return;
    // If not booted via secure mode (e.g., logged out or no session), boot lightweight
    const initIntercomLightweight = async () => {
      let posthogDistinctId: string | null = null;
      try {
        const { boot, getPosthogDistinctId } = await import("../lib/intercom");
        await boot();
        // Attach the PostHog anonymous id as a lead custom attribute so
        // support tooling can resolve flags/experiments for logged-out users.
        posthogDistinctId = await getPosthogDistinctId();
      } catch (err) {
        console.warn(`Intercom lightweight boot: ${err}`);
        return;
      }
      if (!window.Intercom) return;
      if (!intercomBootedRef.current) {
        window.Intercom("boot", {
          api_base: "https://api-iam.intercom.io",
          app_id: "o7y22sae",
          ...(posthogDistinctId
            ? { posthog_distinct_id: posthogDistinctId }
            : {}),
        });
      }
      if (isEmbedded) {
        window.Intercom("update", {
          hide_default_launcher: true,
        });
      }
    };
    initIntercomLightweight();
  }, [isEmbedded, isIntercomAllowed]);

  const pathname = usePathname();

  useEffect(() => {
    // @ts-ignore
    if (!window.Intercom) return;
    // @ts-ignore — Ping Intercom so it re-evaluates URL-based targeting rules (per official SPA docs)
    window.Intercom("update", { last_request_at: parseInt(new Date().getTime() / 1000) });
  }, [pathname]);

  return <>{children}</>;
}
